import React, { memo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { DatePicker } from "../../../../components/ui/datepicker";
import { TimePickerSelect } from "../../../../components/ui/timepicker";
import moment from "moment-timezone";
import { AlertTriangle } from "lucide-react";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import { useKeyHandoverSchedule } from "../context/KeyHandoverScheduleContext";
import { useKeyHandoverStepCounter } from "../hooks/useKeyHandoverStepCounter";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { setPickupTimeSelected } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import { stationDay, stationDayForPicker } from "../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../util/locationTimeZone";

// Tailwind classes replacing previous MUI Box sx styles
const dateTimeWrapperClass = "w-full flex flex-col";

// Defaults and shared helpers
const DEFAULT_START_HOUR = 12;
// Keeping for parity with other modules; not used in this unified step
// const DEFAULT_END_HOUR = 15;
const ZERO = { second: 0, millisecond: 0 } as const;


// Minute step for time picker (30-minute intervals)
const MINUTE_STEP: 5 | 10 | 15 | 20 | 30 | 60 = 30;

/**
 * Maps moment.js weekday numbers (0-6, Sunday=0) to API format (mon, tue, etc.)
 */
const WEEKDAY_MAP: Record<number, string> = {
	0: "sun",
	1: "mon",
	2: "tue",
	3: "wed",
	4: "thu",
	5: "fri",
	6: "sat",
};

/**
 * Get time format string based on language for consistent display
 * @param language - i18n language code
 * @returns moment.js format string
 */
function getTimeFormat(language: string): string {
	switch (language) {
		case 'de': return 'H:mm';      // 9:00, 18:00
		case 'en': return 'h:mm A';     // 9:00 AM, 6:00 PM
		case 'nb': return 'HH:mm';      // 09:00, 18:00
		default: return 'H:mm';
	}
}

/**
 * Get opening hours for a specific date's weekday
 * @param location - Location with opening hours
 * @param date - Date to check (in TZ timezone)
 * @returns Opening hour entry or undefined
 */
function getOpeningHoursForDate(
	location: { openingHours?: Array<{ weekday: string; times?: { fromHour?: number; toHour?: number } }> } | null | undefined,
	date: moment.Moment
) {
	if (!location?.openingHours) return undefined;
	const weekday = WEEKDAY_MAP[date.day()];
	return location.openingHours.find((oh) => oh.weekday === weekday);
}

/**
 * KeyHandoverScheduleStep Component
 * ---------------------------------------------------------------
 * Manages the schedule selection step in the key handover booking flow.
 * 
 * Features:
 * - Date picker with availability filtering
 * - Time picker with opening hours constraints (min: opening + 1h, max: closing)
 * - Duration selector (number of days)
 * - Dynamic pricing calculation
 * - Opening hours info display
 * - Deadline warning with late fee notice
 * 
 * @remarks
 * This component enforces:
 * - Minimum pickup time: 1 hour after location opening
 * - Maximum pickup time: location closing time
 * - No past dates/times selection
 * - Availability-based date filtering
 */
const KeyHandoverScheduleStep = () => {
	const { t, i18n } = useTranslation();
	const { current, total } = useKeyHandoverStepCounter("handoverSchedule");
	const dispatch = useAppDispatch();
	const {
		orderData,
		availabilities,
		selectedGroup,
		currency,
		nextEnabled,
		isLoading,
		setDates,
		setNextEnabled: setNextEnabledFlag,
	} = useKeyHandoverSchedule();
	
	// Get selected location for opening hours filtering
	const selectedLocation = useSelector(
		(state: RootState) => state.bookingCreation.selectedLocation
	);
	// The zone used across availability and date math: the selected
	// location's (#3219), as the server reads the window.
	const TZ = useSelectedLocationTimeZone();
	// Reusable style/class constants to keep UI consistent
	const dateButtonClassName =
		"w-full h-[62px] text-base rounded-[12px] border border-black bg-white px-4 text-black justify-start focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:bg-white";
	const timeSelectClassName =
		"w-full h-[62px] text-base rounded-[12px] border border-black bg-white px-4 text-black focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:bg-white";
	// Single place to define how we map a JS Date to an availability key: the
	// station day with the LABEL the customer clicked. `moment(d).tz(TZ)` read
	// a Tokyo/Athens phone's midnight as the previous Berlin day.
	const availabilityKey = React.useCallback((d: Date) => {
		return stationDay(moment(d), TZ).format("DD.MM.YYYY");
	}, [TZ]);
	// values come from schedule context

	// Loading when availabilities are not yet present; empty when loaded but has no keys
	const isAvailabilitiesLoaded = Boolean(availabilities);
	const isEmptyAvailabilities = isAvailabilitiesLoaded && Object.keys(availabilities as Record<string, unknown>).length === 0;

	// Ensure Next button reflects current selection when revisiting this step
	React.useEffect(() => {
		const desired = Boolean(orderData.checkInDate && orderData.checkOutDate);
		if (desired !== nextEnabled) {
			setNextEnabledFlag(desired);
		}
	}, [nextEnabled, orderData.checkInDate, orderData.checkOutDate, setNextEnabledFlag]);

	// Helpers to normalize/compose dates with time
	const withTime = React.useCallback(
		(base: moment.Moment, hour: number, minute: number) => {
			// Construct a moment in booking TZ from calendar components to avoid DST/keepLocalTime pitfalls
			const inTz = base.clone().tz(TZ);
			return moment.tz(
				{
					year: inTz.year(),
					month: inTz.month(),
					date: inTz.date(),
					hour,
					minute,
					second: ZERO.second,
					millisecond: ZERO.millisecond,
				},
				TZ
			);
		},
		[TZ]
	);

	// Small helpers to avoid repetition
	const updateNextEnabled = React.useCallback(
		(ci: moment.Moment | null | undefined, co: moment.Moment | null | undefined) => {
			setNextEnabledFlag(Boolean(ci && co));
		},
		[setNextEnabledFlag]
	);

	// Helper to set start/end and update next-enabled in one place
	const updateDates = React.useCallback(
		(start: moment.Moment | null, end: moment.Moment | null) => {
			setDates(start, end);
			updateNextEnabled(start, end);
		},
		[setDates, updateNextEnabled]
	);

	const getCurrentDays = React.useCallback(() => {
		if (!orderData.checkInDate || !orderData.checkOutDate) return 1;
		return Math.max(orderData.checkOutDate.diff(orderData.checkInDate, "days"), 1);
	}, [orderData.checkInDate, orderData.checkOutDate]);

	// Currency formatting helper honoring current locale and tenant currency
	const formatCurrency = React.useCallback(
		(amount: number) =>
			new Intl.NumberFormat(i18n.language, {
				style: "currency",
				currency,
			}).format(amount),
		[i18n.language, currency]
	);

	// Price: base (selected service price) + 0.50 € per additional day (first 24h free)
	const computeEstimatedTotalPrice = React.useCallback((days: number) => {
		// Base service price from selected group (lowest duration tier, lowest price)
		const priceScale = selectedGroup?.priceCategoryInfo?.priceScale ?? [];
		const base = priceScale.length
			? (() => {
					let minDuration = Number.POSITIVE_INFINITY;
					let pricesForMin: number[] = [];
					for (const { durationInMinutes, price } of priceScale) {
						if (durationInMinutes < minDuration) {
							minDuration = durationInMinutes;
							pricesForMin = [price ?? 0];
						} else if (durationInMinutes === minDuration) {
							pricesForMin.push(price ?? 0);
						}
					}
					const minPrice = pricesForMin.length ? Math.min(...pricesForMin) : 0;
					return Number(minPrice.toFixed(2));
				})()
			: 0;
		const additionalDays = Math.max(0, days - 1);
		const perAdditional = 0.5;
		const total = base + additionalDays * perAdditional;
		return Number(total.toFixed(2));
	}, [selectedGroup]);

	// Compute minimum selectable HH:mm for a given date: block past times when date is today (location zone)
	// and respect opening hours (must be at least 1 hour after opening)
	const minStartForDate = React.useCallback(
		(date: moment.Moment | null | undefined): string => {
			if (!date) return "00:00";
			
			const now = moment().tz(TZ);
			const dateInTz = date.clone().tz(TZ);
			const isToday = dateInTz.isSame(now, "day");
			
			// Get opening hours for the selected date's weekday
			let minHourFromOpeningHours: number | null = null;
			const openingHour = getOpeningHoursForDate(selectedLocation, dateInTz);
			
			if (openingHour?.times?.fromHour !== undefined) {
				// Add 1 hour to opening time as per requirement
				minHourFromOpeningHours = openingHour.times.fromHour + 1;
			}
			
			// Determine minimum hour based on current time (if today) and opening hours
			let minHour = 0;
			let minMinute = 0;
			
			if (isToday) {
				// If today, can't pick past times
				const minutes = now.minutes();
				const remainder = minutes % MINUTE_STEP;
				let ceilMinutes = remainder === 0 ? minutes : minutes + (MINUTE_STEP - remainder);
				let hour = now.hour();
				if (ceilMinutes >= 60) {
					hour = (hour + 1) % 24;
					ceilMinutes = 0;
				}
				minHour = hour;
				minMinute = ceilMinutes;
			}
			
			// Apply opening hours constraint (opening + 1 hour)
			if (minHourFromOpeningHours !== null) {
				// If opening hours minimum is later, use that
				if (minHourFromOpeningHours > minHour || 
				    (minHourFromOpeningHours === minHour && 0 > minMinute)) {
					minHour = minHourFromOpeningHours;
					minMinute = 0;
				}
			}
			
			const hh = String(minHour).padStart(2, "0");
			const mm = String(minMinute).padStart(2, "0");
			return `${hh}:${mm}`;
		},
		[selectedLocation, TZ]
	);

	// Compute maximum selectable HH:mm for a given date: respect closing hours
	// Since end time = start time + days, we need to ensure start time doesn't exceed closing time
	const maxEndForDate = React.useCallback(
		(date: moment.Moment | null | undefined): string | undefined => {
			if (!date) return undefined;
			
			const dateInTz = date.clone().tz(TZ);
			const openingHour = getOpeningHoursForDate(selectedLocation, dateInTz);
			
			if (openingHour?.times?.toHour !== undefined) {
				// Closing hour is the maximum (no +1 hour buffer for closing time)
				const maxHour = openingHour.times.toHour;
				const hh = String(maxHour).padStart(2, "0");
				return `${hh}:00`;
			}
			
			return undefined;
		},
		[selectedLocation, TZ]
	);

	const handleDropOffDateChange = React.useCallback(
		(date: moment.Moment | null) => {
			if (date) {
				// Preserve previously selected start time (if any), otherwise calculate default
				let preservedHour = orderData.checkInDate?.hour();
				let preservedMinute = orderData.checkInDate?.minute();
				
				// If no previous time, calculate appropriate default
				if (preservedHour === undefined || preservedMinute === undefined) {
					const now = moment().tz(TZ);
					const dateInTz = stationDay(date, TZ);
					const isToday = dateInTz.isSame(now, "day");
					
					if (isToday) {
						// If today, start from current time rounded up to next interval
						const minutes = now.minutes();
						const remainder = minutes % MINUTE_STEP;
						let ceilMinutes = remainder === 0 ? minutes : minutes + (MINUTE_STEP - remainder);
						let hour = now.hour();
						if (ceilMinutes >= 60) {
							hour = (hour + 1) % 24;
							ceilMinutes = 0;
						}
						preservedHour = hour;
						preservedMinute = ceilMinutes;
					} else {
						// Future date - use default 12:00
						preservedHour = DEFAULT_START_HOUR;
						preservedMinute = 0;
					}
				}
				
				// Ensure the time respects minimum constraints (current time + opening hours)
				const tempStart = withTime(stationDay(date, TZ), preservedHour, preservedMinute);
				const minTime = minStartForDate(tempStart);
				const [minHour, minMinute] = minTime.split(':').map(Number);
				
				// If preserved time is before the minimum allowed time, use the minimum instead
				if (preservedHour < minHour || (preservedHour === minHour && preservedMinute < minMinute)) {
					preservedHour = minHour;
					preservedMinute = minMinute;
				}
				
				// The picked calendar day as the station day with that label
				const start = withTime(stationDay(date, TZ), preservedHour, preservedMinute);
				// Preserve previously selected number of days (if both dates set), default to 1
				const prevDays = orderData.checkInDate && orderData.checkOutDate
					? Math.max(orderData.checkOutDate.diff(orderData.checkInDate, "days"), 1)
					: 1;
				const end = withTime(start.clone().add(prevDays, "day"), start.hour(), start.minute());
				updateDates(start, end);
				// Reset time selection flag when date changes - user must confirm time
				dispatch(setPickupTimeSelected(false));
			} else {
				updateDates(null, null);
				dispatch(setPickupTimeSelected(false));
			}
		},
		[orderData.checkInDate, orderData.checkOutDate, updateDates, withTime, dispatch, minStartForDate, TZ]
	);

	// Keep computed availableDates for potential future constraints or UI messaging
	// and used to disable unavailable days in the DatePicker.
	const availableDateSet: Set<string> | undefined = React.useMemo(() => {
		if (!availabilities) return undefined;
		const keys = Object.keys(availabilities).filter((dateKey) => {
			const timeSlots = (availabilities as any)[dateKey];
			return Object.values(timeSlots).some((slot: any) =>
				Object.values(slot as any).some((value: any) => Number(value) > 0)
			);
		});
		// If no available keys yet (initial load or no capacities), don't disable everything.
		return keys.length ? new Set(keys) : undefined;
	}, [availabilities]);

	const isDateDisabled = React.useCallback(
		(date: Date) => {
			// Only enforce disabling when we have at least one known available date.
			if (!availableDateSet) return false;
			// Use the same timezone as used when fetching availabilities (the location's)
			// to avoid off-by-one mismatches when the user's local timezone differs.
			// Availability object is keyed by 'DD.MM.YYYY' (see orderDataStepHelper.ts),
			// so compare using that format to match correctly.
			const key = availabilityKey(date);
			return !availableDateSet.has(key);
		},
		[availableDateSet, availabilityKey]
	);

	return (
		<div className="mb-6">
			{/* Sub headline: Step indicator based on service type */}
			<p className="-mt-5 mb-6 text-xs text-muted-foreground text-center" role="status" aria-live="polite">
				{t("book_location.keys.step_counter", {
					current,
					total,
					defaultValue: "Schritt {{current}} von {{total}}",
				})}
			</p>
			{/* Intro always visible, regardless of availability loading */}
			<p className="mb-3 text-sm text-muted-foreground">
				{t("book_location.keys.order_data.intro", {
					defaultValue:
						"Wählen Sie den Zeitraum, in dem Sie die Schlüsselübergabe mit kolula KEY durchführen möchten.",
				})}
			</p>
			{isAvailabilitiesLoaded && (
				isEmptyAvailabilities ? (
					<div className="pl-5">
						<p>{t("book_location.order_data.error.no_availabilities_for_group")}</p>
					</div>
				) : (
					<>
						{/* Unified first step for both services: drop-off date, start time, and number of days */}
						<div className={dateTimeWrapperClass}>
							<label className="mb-1 block text-sm font-medium text-black">
								{t("book_location.reservation_details.drop_off_date")}
								<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
							</label>
							<DatePicker
								value={orderData.checkInDate ? stationDayForPicker(orderData.checkInDate, TZ) : null}
								onChange={(d) => handleDropOffDateChange(d ? moment(d) : null)}
								buttonClassName={dateButtonClassName}
								isDateDisabled={isDateDisabled}
								showClearInTrigger
							/>

							{/* Start time and number of days controls */}
							<div className="grid grid-cols-2 gap-3 mt-3">
								<div className="flex flex-col">
									<label className="mb-1 block text-xs font-medium text-black">
										{t("general.start_time", { defaultValue: "Startzeit" })}
										<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
									</label>
									<TimePickerSelect
										value={orderData.checkInDate ? orderData.checkInDate.format("HH:mm") : ""}
										onChange={(hhmm: string | null) => {
											if (!hhmm || !orderData.checkInDate) return;
											const [h, m] = hhmm.split(":").map(Number);
											const nextStart = withTime(orderData.checkInDate, h, m);
											const days = getCurrentDays();
											const nextEnd = withTime(nextStart.clone().add(days, "day"), h, m);
											setDates(nextStart, nextEnd);
											dispatch(setPickupTimeSelected(true));
											updateNextEnabled(nextStart, nextEnd);
										}}
										minuteStep={MINUTE_STEP}
										start={minStartForDate(orderData.checkInDate)}
										end={maxEndForDate(orderData.checkInDate)}
										disabled={!orderData.checkInDate}
										className={timeSelectClassName}
										displayFormat="locale"
										timeZone={TZ}
									/>
								</div>
								<div className="flex flex-col">
									<label className="mb-1 block text-xs font-medium text-black">
										{t("book_location.reservation_details.number_of_days", { defaultValue: "Anzahl der Tage" })}
										<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
									</label>
									<div className="flex items-center gap-0">
										<button
											type="button"
											className="h-[62px] w-[62px] rounded-l-[12px] rounded-r-none border border-black bg-background text-black text-2xl leading-none flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 relative focus:z-10 disabled:opacity-50"
											onClick={() => {
												if (!orderData.checkInDate) return;
												const days = Math.max(1, getCurrentDays() - 1);
												const h = orderData.checkInDate.hour();
												const m = orderData.checkInDate.minute();
												const newEnd = withTime(orderData.checkInDate.clone().add(days, "day"), h, m);
												setDates(orderData.checkInDate, newEnd);
												updateNextEnabled(orderData.checkInDate, newEnd);
											}}
											disabled={!orderData.checkInDate}
											aria-label={t("general.decrease", { defaultValue: "Verringern" })}
										>
											−
										</button>
										<input
											type="number"
											min={1}
											step={1}
											value={getCurrentDays()}
											onChange={(e) => {
												const raw = parseInt(e.target.value, 10);
												const days = isNaN(raw) ? 1 : Math.max(raw, 1);
												if (!orderData.checkInDate) return;
												const h = orderData.checkInDate.hour();
												const m = orderData.checkInDate.minute();
												const newEnd = withTime(orderData.checkInDate.clone().add(days, "day"), h, m);
												setDates(orderData.checkInDate, newEnd);
												updateNextEnabled(orderData.checkInDate, newEnd);
											}}
											className={`${timeSelectClassName} text-center rounded-none border-l-0 border-r-0 relative focus:z-10`}
											disabled={!orderData.checkInDate}
											aria-label={t("book_location.reservation_details.number_of_days", { defaultValue: "Anzahl der Tage" })}
										/>
										<button
											type="button"
											className="h-[62px] w-[62px] rounded-r-[12px] rounded-l-none border border-black bg-background text-black text-2xl leading-none flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 relative focus:z-10 disabled:opacity-50"
											onClick={() => {
												if (!orderData.checkInDate) return;
												const days = getCurrentDays() + 1;
												const h = orderData.checkInDate.hour();
												const m = orderData.checkInDate.minute();
												const newEnd = withTime(orderData.checkInDate.clone().add(days, "day"), h, m);
												setDates(orderData.checkInDate, newEnd);
												updateNextEnabled(orderData.checkInDate, newEnd);
											}}
											disabled={!orderData.checkInDate}
											aria-label={t("general.increase", { defaultValue: "Erhöhen" })}
										>
											+
										</button>
									</div>
								</div>
							</div>

							{/* Opening hours info hint - full width below the grid */}
							{orderData.checkInDate && selectedLocation?.openingHours && (() => {
								const dateInTz = orderData.checkInDate.clone().tz(TZ);
								const openingHour = getOpeningHoursForDate(selectedLocation, dateInTz);
								
								if (openingHour?.times?.fromHour !== undefined && openingHour?.times?.toHour !== undefined) {
									const timeFormat = getTimeFormat(i18n.language);
									const openingTime = moment({ hour: openingHour.times.fromHour, minute: 0 }).format(timeFormat);
									const minTime = moment({ hour: openingHour.times.fromHour + 1, minute: 0 }).format(timeFormat);
									const closingTime = moment({ hour: openingHour.times.toHour, minute: 0 }).format(timeFormat);
									
									return (
										<p className="mt-2 text-xs text-muted-foreground">
											{t("book_location.keys.order_data.opening_hours_info", {
												defaultValue: "Öffnung: {{openingTime}} Uhr, früheste Abholung: {{minTime}} Uhr, Schließung: {{closingTime}} Uhr",
												openingTime,
												minTime,
												closingTime
											})}
										</p>
									);
								}
								return null;
							})()}

							{/* Info: price impact when more than 1 day is selected */}
							{(() => {
								const days = getCurrentDays();
								const additionalDays = Math.max(0, days - 1);
								if (!orderData.checkInDate || !orderData.checkOutDate || additionalDays <= 0) return null;
								const totalPrice = computeEstimatedTotalPrice(days);
								return (
									<p className="mt-3 text-sm text-muted-foreground">
										<Trans
											i18nKey="book_location.keys.order_data.extension_price_info"
											values={{ count: additionalDays, additionalDays, totalPrice: formatCurrency(totalPrice) }}
											components={{ bold: <strong className="font-semibold" /> }}
										/>
									</p>
								);
							})()}

							{/* Deadline and late pickup fee notice box */}
							{orderData.checkInDate && orderData.checkOutDate && (() => {
								// Display the deadline in the booking timezone to match user selection
								const deadlineMoment = orderData.checkOutDate.clone().tz(TZ).locale(i18n.language);
								const dateStr = deadlineMoment.format("L");
								const timeStr = deadlineMoment.format("LT");
								const deadlineDisplay = i18n.language.startsWith("de")
									? `${dateStr} ${timeStr} Uhr`
									: `${dateStr} ${timeStr}`;
								return (
									<div className="mt-3 rounded-[12px] border border-black bg-gray-50 p-3 text-sm text-foreground flex items-start gap-3">
										<AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
										<div className="flex-1">
											<Trans
												i18nKey="book_location.keys.order_data.deadline_notice"
												values={{
													deadline: deadlineDisplay,
													fee: formatCurrency(20),
												}}
												components={{ bold: <strong className="font-semibold" />, br: <br /> }}
											/>
										</div>
									</div>
								);
							})()}
						</div>
					</>
				)
			)}
			<LoaderOverlay open={isLoading} />
		</div>
	);
};

export default memo(KeyHandoverScheduleStep);
